/**
 * ContextLevelSection — one standard, collapsible "level" of injected context.
 *
 * The View Context drawer shows several kinds of context; we name them as numbered
 * LEVELS so users (and support) can say "Level 2" unambiguously:
 *
 *   Level 1 · RULES      — always-injected governed instructions (DDB KNOWLEDGE).
 *   Level 2 · REFERENCE  — vector-retrieved chunks (RAG); needs a Run to populate.
 *   Level 3 · Dynamic    — ticket/keyword-threaded pool memory; needs a Run.
 *
 * Every level renders with the SAME chrome so the panel reads as one system:
 *   ┌ header row: ▸ Level N · NAME · <meta peek>      [status chip]
 *   └ expanded body (click header / "expand"): the full content
 *
 * The component is presentational + generic. The parent passes a ``status``
 * ('ready' | 'await-trigger' | 'empty' | 'loading' | 'error') and the body; this
 * file owns the collapse/expand + the unified look only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/** Accent per level — kept subtle + consistent (ring/text only, no loud fills). */
const LEVEL_ACCENT = {
  1: { dot: 'bg-blue-500', text: 'text-blue-700', ring: 'ring-blue-100', soft: 'bg-blue-50' },
  2: { dot: 'bg-violet-500', text: 'text-violet-700', ring: 'ring-violet-100', soft: 'bg-violet-50' },
  3: { dot: 'bg-teal-500', text: 'text-teal-700', ring: 'ring-teal-100', soft: 'bg-teal-50' },
  4: { dot: 'bg-amber-500', text: 'text-amber-700', ring: 'ring-amber-100', soft: 'bg-amber-50' },
  // SKILLS — a lettered (non-numbered) level between RULES and REFERENCE; fuchsia matches the SKILL chip.
  S: { dot: 'bg-fuchsia-500', text: 'text-fuchsia-700', ring: 'ring-fuchsia-100', soft: 'bg-fuchsia-50' },
};
const DEFAULT_ACCENT = { dot: 'bg-gray-400', text: 'text-gray-700', ring: 'ring-gray-100', soft: 'bg-gray-50' };

/** Small right-aligned status chip; mirrors the section's state in one glance. */
function StatusChip({ status, accent }) {
  if (status === 'ready') {
    return (
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${accent.soft} ${accent.text}`}>
        loaded
      </span>
    );
  }
  if (status === 'await-trigger') {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
        Run to load
      </span>
    );
  }
  if (status === 'loading') {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
        loading…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">
        error
      </span>
    );
  }
  // 'empty'
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-400">
      none
    </span>
  );
}

/**
 * @param {object} props
 * @param {number} props.level                 — 1, 2, 3, …
 * @param {string} props.name                  — "RULES" | "REFERENCE" | "Dynamic"
 * @param {string} [props.subtitle]            — one-liner under the title (e.g. "always injected").
 * @param {string} [props.peek]                — short preview shown collapsed (1-2 lines, truncated).
 * @param {'ready'|'await-trigger'|'empty'|'loading'|'error'} [props.status]
 * @param {boolean} [props.defaultExpanded]
 * @param {boolean} [props.expandable]         — when false, no expand affordance (e.g. await-trigger).
 * @param {React.ReactNode} [props.children]   — full content, shown when expanded.
 * @param {string} [props.testId]
 */
export default function ContextLevelSection({
  level,
  name,
  subtitle = '',
  peek = '',
  status = 'empty',
  defaultExpanded = false,
  expandable = true,
  children,
  testId,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Auto-open when ``defaultExpanded`` rises to true (e.g. a Run populates this
  // level) — but never auto-collapse a section the user opened/closed manually.
  const prevDefault = useRef(defaultExpanded);
  useEffect(() => {
    if (defaultExpanded && !prevDefault.current) setExpanded(true);
    prevDefault.current = defaultExpanded;
  }, [defaultExpanded]);
  const accent = LEVEL_ACCENT[level] || DEFAULT_ACCENT;
  const canExpand = expandable && status !== 'await-trigger' && status !== 'empty';
  const open = canExpand && expanded;

  const tid = testId || `ctx-level-${level}`;

  return (
    <section
      className={`overflow-hidden rounded-lg border border-gray-200 bg-white ring-1 ${accent.ring}`}
      data-testid={tid}
    >
      <button
        type="button"
        onClick={() => canExpand && setExpanded((v) => !v)}
        disabled={!canExpand}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${
          canExpand ? 'hover:bg-gray-50' : 'cursor-default'
        }`}
        data-testid={`${tid}-header`}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''} ${
            canExpand ? '' : 'opacity-0'
          }`}
          aria-hidden
        />
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${accent.dot}`} aria-hidden />
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {typeof level === 'number' ? `Level ${level}` : 'Context'}
          </span>
          <span className={`shrink-0 text-xs font-semibold ${accent.text}`}>{name}</span>
          {subtitle ? (
            <span className="truncate text-[10px] text-gray-400">· {subtitle}</span>
          ) : null}
        </span>
        <StatusChip status={status} accent={accent} />
      </button>

      {/* Collapsed peek — only when there's content and we're not expanded. */}
      {!open && peek ? (
        <div className="border-t border-gray-100 px-3 py-1.5" data-testid={`${tid}-peek`}>
          <p className="truncate text-[11px] italic text-gray-500">{peek}</p>
        </div>
      ) : null}

      {/* Expanded body. */}
      {open ? (
        <div className="border-t border-gray-100 px-3 py-2" data-testid={`${tid}-body`}>
          {children}
        </div>
      ) : null}
    </section>
  );
}
